import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { fetchUpcomingSessions } from "../../features/sessions/slice-extensions/sessions-slice-extension";
import {
  selectUpcomingSessionsGroupedByDate,
  selectUpcomingSessionsStatus,
  selectHasUpcomingSessions,
  selectUpcomingSessionsInitializationStatus,
} from "../../features/sessions/selectors/upcomingSessionsSelectors";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { convertSessionTimeToUserTimezone } from "../../features/utils/timezoneUtils";
import { SessionDetailsInfo } from "../SessionDetailsInfo";
import LoadingSpinner from "../LoadingSpinner";
import DateDisplayCard from "../date-display-card/DateDisplayCard";
import { selectTimeZoneFromLocationInfo } from "../../features/localization/localizationSlice";
import { ROUTES } from "../../routes";

const UpcomingSessionsCard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const upcomingSessionsGroupedByDate = useAppSelector(
    selectUpcomingSessionsGroupedByDate
  );
  const upcomingSessionsStatus = useAppSelector(selectUpcomingSessionsStatus);
  const hasUpcomingSessions = useAppSelector(selectHasUpcomingSessions);
  const initializationStatus = useAppSelector(
    selectUpcomingSessionsInitializationStatus
  );

  useEffect(() => {
    if (initializationStatus === "not-initialized") {
      dispatch(fetchUpcomingSessions());
    }
  }, [dispatch, initializationStatus]);

  const userCurrentTimeZone = useAppSelector(selectTimeZoneFromLocationInfo);
  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <svg
            className="lg:size-16"
            fill="none"
            height="48"
            viewBox="0 0 48 48"
            width="48"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12.75 10.5C13.1642 10.5 13.5 10.8358 13.5 11.25C13.5 11.6642 13.1642 12 12.75 12H12C11.1716 12 10.5 12.6716 10.5 13.5V34.5C10.5 35.3284 11.1716 36 12 36H19.9082C20.1909 36 20.4442 36.174 20.5605 36.4316C20.7804 36.9187 20.4406 37.4998 19.9062 37.5H12C10.3431 37.5 9 36.1569 9 34.5V13.5C9 11.8431 10.3431 10.5 12 10.5H12.75ZM16.5 28.5C17.3284 28.5 18 29.1716 18 30C18 30.8284 17.3284 31.5 16.5 31.5C15.6716 31.5 15 30.8284 15 30C15 29.1716 15.6716 28.5 16.5 28.5ZM16.5 21.75C17.3284 21.75 18 22.4216 18 23.25C18 24.0784 17.3284 24.75 16.5 24.75C15.6716 24.75 15 24.0784 15 23.25C15 22.4216 15.6716 21.75 16.5 21.75ZM24 21.75C24.1749 21.75 24.2184 21.9611 24.0811 22.0693C23.5957 22.4518 23.1405 22.8707 22.7197 23.3223C22.6401 23.4078 22.5 23.3669 22.5 23.25C22.5 22.4216 23.1716 21.75 24 21.75ZM36 10.5C37.6569 10.5 39 11.8431 39 13.5V20.71C39 21.2838 38.3258 21.6161 37.8389 21.3125C37.6317 21.1833 37.5 20.958 37.5 20.7139V13.5C37.5 12.6716 36.8284 12 36 12H35.25C34.8358 12 34.5 11.6642 34.5 11.25C34.5 10.8358 34.8358 10.5 35.25 10.5H36ZM35.25 16.5C35.6642 16.5 36 16.8358 36 17.25C36 17.6642 35.6642 18 35.25 18H12.75C12.3358 18 12 17.6642 12 17.25C12 16.8358 12.3358 16.5 12.75 16.5H35.25ZM16.502 7.5C17.2466 7.5 17.9971 7.875 17.9971 9C17.9971 10.125 18.002 11.5252 18.002 12C17.9969 13.125 17.257 13.5 16.502 13.5C15.747 13.4999 14.9969 13.1249 15.002 12C15.002 11.5252 15.0029 10.125 15 9C14.9971 7.87508 15.7466 7.50005 16.502 7.5ZM31.502 7.5C32.2466 7.5 32.9971 7.875 32.9971 9C32.9971 10.125 33.002 11.5252 33.002 12C32.9969 13.125 32.257 13.5 31.502 13.5C30.747 13.4999 29.9969 13.1249 30.002 12C30.002 11.5252 30.0029 10.125 30 9C29.9971 7.87508 30.7466 7.50005 31.502 7.5ZM27.75 10.5C28.1642 10.5 28.5 10.8358 28.5 11.25C28.5 11.6642 28.1642 12 27.75 12H20.25C19.8358 12 19.5 11.6642 19.5 11.25C19.5 10.8358 19.8358 10.5 20.25 10.5H27.75Z"
              fill="#808080"
            />
            <path
              d="M31.5 21C37.299 21 42 25.701 42 31.5C42 37.299 37.299 42 31.5 42C25.701 42 21 37.299 21 31.5C21 25.701 25.701 21 31.5 21ZM30.6064 36C30.3279 36.0002 30.1469 36.2937 30.2715 36.543L30.415 36.8291C30.6206 37.2402 31.0404 37.5 31.5 37.5C31.9596 37.5 32.3794 37.2402 32.585 36.8291L32.7285 36.543C32.8531 36.2937 32.6721 36.0002 32.3936 36H30.6064ZM31.2148 25.5C29.6737 25.5001 28.383 26.6677 28.2295 28.2012L27.8291 32.2129C27.7801 32.7026 27.4831 33.1334 27.043 33.3535L26.9912 33.3789C26.5368 33.6062 26.25 34.071 26.25 34.5791C26.25 34.9496 26.5504 35.25 26.9209 35.25H36.0791C36.4496 35.25 36.75 34.9496 36.75 34.5791C36.75 34.071 36.4632 33.6062 36.0088 33.3789L35.957 33.3535C35.5169 33.1334 35.2199 32.7026 35.1709 32.2129L34.7705 28.2012C34.617 26.6677 33.3263 25.5001 31.7852 25.5H31.2148Z"
              fill="#9966FF"
            />
          </svg>
          <h2>Upcoming Sessions</h2>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(ROUTES.CREATE_SESSION)}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              className="fill-current"
              d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
            ></path>
          </svg>

          <span>Create Session</span>
        </button>
      </div>
      <p className="mt-2 lg:text-xl">
        Browse upcoming session times and choose the one that fits your
        schedule.
      </p>
      <div className="mt-4 space-y-4 lg:mt-6 lg:space-y-6">
        {upcomingSessionsStatus === LoadingStatus.Loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner text="Loading upcoming sessions..." />
          </div>
        ) : !hasUpcomingSessions ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4">
            <p className="text-grey-40 dark:text-grey-80">
              No upcoming sessions found. Create a new session to get started.
            </p>
          </div>
        ) : (
          upcomingSessionsGroupedByDate.map((group) => (
            <div key={group.dateLabel} className="flex h-full gap-4 lg:gap-6">
              <DateDisplayCard
                dateLabel={group.dateLabel}
                dateDisplay={group.dateDisplay}
              />
              <div>
                {group.sessions.map((session, index) => {
                  // Convert session time to user's timezone
                  const { userTimezoneRange } =
                    convertSessionTimeToUserTimezone(
                      session.dateStart,
                      session.timeLocal,
                      userCurrentTimeZone || "UTC",
                      session.duration
                    );
                  const timeRange = userTimezoneRange;
                  // Determine variant based on session index
                  const borderColorVariant =
                    index % 2 === 0 ? "aqua" : "orange";
                  return (
                    <SessionDetailsInfo
                      key={session.id}
                      title={session.title}
                      time={timeRange}
                      borderColorVariant={borderColorVariant}
                    />
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="mt-4 lg:mt-6">
        <button
          className="btn btn-text"
          onClick={() => navigate("/sessions?view=upcoming")}
        >
          <span>View All Upcoming Sessions</span>
          <svg
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9.75781 7.03035C10.1043 6.9375 10.4749 7.03645 10.7285 7.29011L14.6982 11.2901C14.8875 11.4779 14.9941 11.7335 14.9941 12.0001C14.9941 12.2667 14.8875 12.5223 14.6982 12.71L10.6982 16.71C10.512 16.8948 10.2604 16.999 9.99805 17.0001C9.73224 17.0016 9.4768 16.8972 9.28809 16.71C9.09877 16.5223 8.99219 16.2667 8.99219 16.0001C8.99219 15.7334 9.09877 15.4779 9.28809 15.2901L12.5781 12.0001L9.30762 8.71004C9.05418 8.45643 8.95512 8.08665 9.04785 7.74031C9.14066 7.39393 9.41149 7.12329 9.75781 7.03035Z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default UpcomingSessionsCard;
